'use strict';
import Http from 'axios';
import React from 'react';
import { If } from 'jsx-control-statements';

/**
 * Screen for choosing a new password after a user has successfully
 * completed one of the methods for authenticating themselves. Interactive
 * guidance is provided to help a user choose a strong password, and validate
 * it before accepting the change.
 *
 * @export
 * @class PasswordChooser
 * @extends {React.Component}
 */
export default class PasswordChooser extends React.Component {

    constructor(props) {
        super(props);

        // Set default state
        this.state = {
            password: '',
            passwordConfirm: '',
            samples: {
                generating: true,
                passwords: [],
                passphrases: []
            },
            strength: null
        };

        // Bind non-React functions
        this.checkPasswordStrength = this.checkPasswordStrength.bind(this);
        this.generatePasswordSamples = this.generatePasswordSamples.bind(this);
        this.handlePasswordChange = this.handlePasswordChange.bind(this);
        this.handlePasswordConfirmChange = this.handlePasswordConfirmChange.bind(this);
        this.renderPasswordMatchResult = this.renderPasswordMatchResult.bind(this);
        this.renderStrengthMeter = this.renderStrengthMeter.bind(this);
        this.savePassword = this.savePassword.bind(this);
    }

    componentWillMount() {
        // Initial password samples
        this.generatePasswordSamples();
    }

    /**
     * Submit a user password to the server for a strength check, and update
     * the component state with the resulting report.
     */
    async checkPasswordStrength() {
        try {
            // Execute web request to strength check the password
            let response = await Http.post('/data/credential/validate', { credential: this.state.password });

            // Apply component state
            this.setState({ strength: response.data });
        }
        catch(error) {
            // Activate the error view
            // Console.log('HTTP web service /data/credential/validate returned error', error);
            this.props.router.push(`/error`);
        }
    }

    /**
     * Generate samples of strong passwords server-side to help guide the user
     * to choosing a difficult to crack password.
     */
    async generatePasswordSamples() {
        try {
            // Execute server side service to generate passwords
            let response = await Http.get('/util/genpws');

            // Update component state with sample passwords
            this.setState({
                samples: {
                    generating: false,
                    passwords: response.data.passwords,
                    passphrases: response.data.passphrases
                }
            });
        }
        catch(error) {
            // Activate the error view
            // Console.log('HTTP web service /util/genpws returned error', error);
            this.props.router.push(`/error`);
        }
    }

    /**
     * Event handler that responds to changes when the user types in a new
     * password (1st text box)
     *
     * @param {Object} event
     */
    handlePasswordChange(event) {
        // Update component state
        this.setState({
            password: event.target.value,
            passwordsMatch: (this.state.passwordConfirm) ? this.state.passwordConfirm === event.target.value : false,
            passwordSampleUsed: this.isPasswordSampled(event.target.value)
        });

        // Clear an strength check timer if found
        if(this.passwordCheckTimer) {
            clearTimeout(this.passwordCheckTimer);
        }

        // Create a new timer
        this.passwordCheckTimer = setTimeout(this.checkPasswordStrength, 500);
    }

    /**
     * Event handler that responses to changes when the user type in the
     * new password a second time to confirm it (2nd text box)
     *
     * @param {Object} event
     */
    handlePasswordConfirmChange(event) {
        // Update component state
        this.setState({
            passwordConfirm: event.target.value,
            passwordsMatch: (this.state.password) ? this.state.password === event.target.value : false
        });
    }

    isPasswordSampled(password) {
        // Verify that the user has not copied any of the sample passwords
        let allSamples = this.state.samples.passwords.concat(
            this.state.samples.passphrases);

        return allSamples.includes(password);
    }

    /**
     * Check the latest password strength report stored in component state,
     * and return a boolean value if the password is suitable to be saved
     * to the users account.
     *
     * @returns {Boolean} True if all requirements are met, false if not
     */
    isPasswordValid() {
        if(this.state.strength) {
            return (this.state.strength.strengthCode === 'STRONG'
                || this.state.strength.strengthCode === 'VERY_STRONG'
                || this.state.strength.strengthCode === 'REASONABLE')
                && this.state.passwordsMatch
                && !(this.state.passwordSampleUsed);
        }
        return false;
    }

    preventSampleCopying(event) {
        // Prevent normal behavior
        event.preventDefault();

        // Notify the user
        alert('No copying of the sample passwords. They are provided only as suggestions');
    }

    preventPasswordCopyPaste(event) {
        console.log(event)
        // Prevent normal behavior
        event.preventDefault();

        // Notify the user
        alert('No copying or pasting your password. You must enter it manually.');
    }

    confirmStartOver(event) {
        var answer = confirm('Are you sure you want to start over? Starting over will bring you to the first page and you will have to confirm your identity again. To try a new password, simply clear the input fields.');

        if (answer === false) {
            event.preventDefault();
        };
    }

    /**
     * Submit the new users password server-side to applied to their account
     */
    async savePassword() {
        if(this.state.passwordSampleUsed) {
            return alert('No copying of the sample passwords. They are provided only as suggestions');
        }

        try {
            // Execute web request to apply password change
            await Http.post(`/data/credential/apply/${fhda.sessionId}`, { credential: this.state.password });

            // Change to the success view
            this.props.router.push(`/success`);
        }
        catch(error) {
            // Activate the error view
            // Console.log('HTTP web service /data/credential/apply returned error', error);
            this.props.router.push(`/error`);
        }
    }

    /**
     * Render a warning if the two password fields do not match
     */
    renderPasswordMatchResult() {
        // If both password fields are filled in, do they match?
        if(!(this.state.passwordsMatch) && this.state.passwordConfirm) {
            return <div id="password-nomatch-warning" className="bg-warning text-danger">
                <i className="fa fa-exclamation"></i> Passwords do not match
            </div>;
        }
        else if(this.state.passwordSampleUsed) {
            return <div id="password-nomatch-warning" className="bg-warning text-danger">
                <i className="fa fa-exclamation"></i> Re-using the sample passwords is not permitted
            </div>;
        }
    }

    /**
     * Render a set of password samples to help guide the user to choosing
     * a strong and secure password
     */
    renderPasswordSamples() {
        return <div>
            <div>
                <strong>Passwords (good):</strong>
                {this.state.samples.passwords.map(password => {
                    return <div key={password} onCopy={this.preventSampleCopying}><code>{password}</code></div>;
                })}
            </div>

            <div>
                <strong>Passphrases (best):</strong>
                {this.state.samples.passphrases.map(phrase => {
                    return <div key={phrase} onCopy={this.preventSampleCopying}><code>{phrase}</code></div>;
                })}
            </div>
        </div>;
    }

    /**
     * Render a Boostrap progress bar and associated label text to provide
     * a visual indicator of the strength of the users chosen password
     */
    renderStrengthMeter() {
        // Translate Tai strength code into percentage
        let percentage = 0;
        let progressBarClasses = 'progress-bar progress-bar-striped ';
        let strengthRatingTextClasses = '';
        let strengthRatingText = '';
        if(this.state.strength) {
            if(this.state.strength.strengthCode === 'VERY_WEAK') {
                percentage = 20;
                progressBarClasses += 'progress-bar-danger';
                strengthRatingTextClasses = 'text-danger';
                strengthRatingText = 'Very weak (choose a password with more letters and numbers)';
            }
            else if(this.state.strength.strengthCode === 'WEAK') {
                percentage = 40;
                progressBarClasses += 'progress-bar-danger';
                strengthRatingTextClasses = 'text-danger';
                strengthRatingText = 'Weak (choose a password with more letters and numbers)';
            }
            else if(this.state.strength.strengthCode === 'REASONABLE') {
                percentage = 60;
                progressBarClasses += 'progress-bar-warning';
                strengthRatingTextClasses = 'text-warning';
                strengthRatingText = 'Reasonable (a password with more letters, numbers, or special characters is reccomended but not required)';
            }
            else if(this.state.strength.strengthCode === 'STRONG') {
                percentage = 80;
                progressBarClasses += 'progress-bar-success';
                strengthRatingTextClasses = 'text-success';
                strengthRatingText = 'Strong';
            }
            else if(this.state.strength.strengthCode === 'VERY_STRONG') {
                percentage = 100;
                progressBarClasses += 'progress-bar-success';
                strengthRatingTextClasses = 'text-success';
                strengthRatingText = 'Very strong';
            }
        }

        let progressStyle = {
            width: `${percentage}%`
        };

        return <div>
            <label>Strength Test</label>
            {(percentage > 0) ? <div className={strengthRatingTextClasses}>
                {strengthRatingText}
            </div> : null}
            <div className="progress">
                <div className={progressBarClasses} role="progressbar" aria-valuenow={percentage} aria-valuemin="0" aria-valuemax="100" style={progressStyle}>
                    <span className="sr-only">{percentage}% Complete</span>
                </div>
            </div>
        </div>;
    }

    render () {
        return <div className="row">
            <div className="col-md-12">
                <div className="alert alert-success">
                    <strong>You are successfully authenticated!</strong><br/>
                    Please follow the directions below to choose a new password.
                </div>
                <p>
                    To protect your privacy and keep your data secure, we require
                    passwords to be rated <strong>Reasonable</strong> or higher. The strength
                    test shown below will help you choose a secure password.
                </p>
                <p>
                    A quality password is one that is complicated enough that makes it
                    difficult to guess or decipher. It can be a random mix of characters,
                    or even a short phrase of words. It can contain numbers, a mix of upper case
                    and lower case letters, and must be at least 8 characters long.
                </p>
                <div className="panel panel-default">
                    <div className="panel-heading">
                        <h3 className="panel-title">Examples of Strong Passwords</h3>
                    </div>
                    <div className="panel-body">
                        <If condition={this.state.samples.generating}>
                            <i className="fa fa-cog fa-spin fa-fw"></i> Generating samples...
                        </If>
                        <If condition={!this.state.samples.generating}>
                            {this.renderPasswordSamples()}
                        </If>
                    </div>
                </div>
                <div className="form-group">
                    <label htmlFor="password">New Password</label>
                    <input type="password" className="form-control" name="password" onChange={this.handlePasswordChange} />
                </div>
                <div className="form-group">
                    <label htmlFor="passwordConfirm">Confirm Password</label>
                    <input type="password" className="form-control" name="password-confirm" onChange={this.handlePasswordConfirmChange} onPaste={this.preventPasswordCopyPaste} />
                </div>

                {this.renderPasswordMatchResult()}

                {this.renderStrengthMeter()}

                <button className="btn btn-success" onClick={this.savePassword} disabled={!this.isPasswordValid()}>
                    <i className="fa fa-floppy-o"></i> Save
                </button>

                <a className="btn btn-default" onClick={this.confirmStartOver} href="/resetpwd">
                    <i className="fa fa-refresh"></i> Start Over
                </a>
            </div>
        </div>;
    }
}


// WEBPACK FOOTER //
// ./src/react/views/PasswordChooser.jsx